// components/RecordPlayer/DiscussionPanel.jsx
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { getEmojiUrl, isEmoji } from '../../utils/emojiUtils';

const DiscussionPanel = ({ roomid, currentTime, chatInfo }) => {
  const [messageGroups, setMessageGroups] = useState([]);
  const [filterMessages, setFilterMessages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null); // 控制预览图片显示
  const discussionListRef = useRef(null);
  const isUserScrolledRef = useRef(false);
  const preMessagesLengthRef = useRef(0);

  // 将消息按“时间差 > 60秒”进行分组
  useEffect(() => {
    let visibleMessages = chatInfo
      .filter((msg) => msg.milliseconds <= currentTime && msg.data.content !== '')
      .sort((a, b) => a.milliseconds - b.milliseconds); // 按时间升序

    setFilterMessages(visibleMessages);

  }, [currentTime, chatInfo]);

  useEffect(() => {
    if (filterMessages.length === 0) {
      setMessageGroups([]);
      return;
    }

    const groups = [];
    let currentGroup = {
      time: filterMessages[0].data.timestamp,
      messages: [filterMessages[0]],
    };

    for (let i = 1; i < filterMessages.length; i++) {
      const msg = filterMessages[i];
      const prevMsg = filterMessages[i - 1];
      const timeDiff = msg.milliseconds - prevMsg.milliseconds;

      if (timeDiff > 60) {
        groups.push(currentGroup);
        currentGroup = {
          time: msg.data.timestamp,
          messages: [msg],
        };
      } else {
        currentGroup.messages.push(msg);
      }
    }
    groups.push(currentGroup);
    setMessageGroups(groups);

  }, [filterMessages.length]);

  useLayoutEffect(() => {
    if (!isUserScrolledRef.current) {
      scrollToBottom();
    }
  }, [messageGroups]);

  // 滚动到底部
  const scrollToBottom = () => {
    if (discussionListRef.current) {
      discussionListRef.current.scrollTop = discussionListRef.current.scrollHeight;
    }
  };

  // 监听滚动
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    isUserScrolledRef.current = !isNearBottom;
  };

  // 回到最新消息
  const handleBackToLive = () => {
    isUserScrolledRef.current = false;
    scrollToBottom();
  };

  // 格式化时间
  const formatTime = (milliseconds) => {
    const date = new Date(milliseconds);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // 图片点击处理函数
  const handleImageClick = (src) => {
    setPreviewImage(src);
  };

  // 关闭预览
  const closePreview = () => {
    setPreviewImage(null);
  };

  // 支持 Esc 关闭
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setPreviewImage(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className="discussion-panel">
      <div className="discussion-header">
        <h3>课堂讨论</h3>
        <span>{messageGroups.reduce((sum, g) => sum + g.messages.length, 0)}条消息</span>
      </div>

      <div
        className="discussion-list"
        ref={discussionListRef}
        onScroll={handleScroll}
      >
        {messageGroups.length > 0 ? (
          messageGroups.map((group, groupIndex) => (
            <React.Fragment key={group.time}>
              <div className="time-divider">
                <span className="time-divider-text">
                  {formatTime(group.time)}
                </span>
              </div>

              {group.messages.map((msg) => (
                <div key={msg.id} className="discussion-item">
                  <div className="discussion-author">
                    <strong>{msg.data.operatorUserName}</strong>
                  </div>
                  <div className="discussion-content">
                    {msg.type === 385 ? (
                      // 文本消息
                      isEmoji(msg.data.content) ? (
                        <img
                          src={getEmojiUrl(msg.data.content)}
                          alt="emoji"
                          className="chat-emoji-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '[图片加载失败]';
                          }}
                        />
                      ) : (
                        <span className="text">{msg.data.content}</span>
                      )
                    ) : (
                      // 图片消息（支持点击放大）
                      <img
                        src={msg.data.content.slideUrl}
                        alt="聊天图片"
                        className="chat-image"
                        onClick={() => handleImageClick(msg.data.content.mediumUrl)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '[图片加载失败]';
                        }}
                        style={{ cursor: 'pointer' }} // 提示可点击
                      />
                    )}
                  </div>
                </div>
              ))}
            </React.Fragment>
          ))
        ) : (
          <div className="empty-discussion">
            <p>当前时间点没有讨论内容</p>
          </div>
        )}
      </div>

      {/* 回到最新按钮 */}
      {isUserScrolledRef.current && (
        <button className="back-to-live-btn" onClick={handleBackToLive}>
          回到最新
        </button>
      )}

      {/* 图片预览模态框 */}
      {previewImage && (
        <div
          className="image-preview-modal"
          onClick={closePreview}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            cursor: 'pointer',
          }}
        >
          <img
            src={previewImage}
            alt="预览大图"
            style={{
              maxHeight: '90vh',
              maxWidth: '90vw',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()} // 阻止事件冒泡
          />
        </div>
      )}
    </div>
  );
};

export default DiscussionPanel;